import { useState, useRef, useEffect } from 'react'
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react'
import { format } from 'date-fns'

// DatePicker with clickable year selection
const DatePicker = ({ value, onChange, placeholder = 'Select date', className = '', maxDate, minDate }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [selectedDate, setSelectedDate] = useState(value ? new Date(value) : null)
  const [currentMonth, setCurrentMonth] = useState(selectedDate || new Date())
  const [showYearPicker, setShowYearPicker] = useState(false)
  const [yearRangeStart, setYearRangeStart] = useState((selectedDate || new Date()).getFullYear() - 6)
  const datePickerRef = useRef(null)

  // Update local state from incoming `value` prop. Use an asynchronous
  // update (setTimeout) to avoid triggering a synchronous setState within
  // the effect body — this addresses the linter warning about cascading
  // renders while preserving the intended synchronization behavior.
  useEffect(() => {
    if (value) {
      const d = new Date(value)
      const t = setTimeout(() => {
        setSelectedDate(d)
        setCurrentMonth(d)
        setYearRangeStart(d.getFullYear() - 6)
      }, 0)

      return () => clearTimeout(t)
    }
    return undefined
  }, [value])

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (datePickerRef.current && !datePickerRef.current.contains(event.target)) {
        setIsOpen(false)
        setShowYearPicker(false)
      }
    }
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  const handleDateSelect = (date) => {
    if (minDate && date < new Date(minDate)) return
    if (maxDate && date > new Date(maxDate)) return

    setSelectedDate(date)
    onChange(format(date, 'yyyy-MM-dd'))
    setIsOpen(false)
    setShowYearPicker(false)
  }

  const getDaysInMonth = (date) => {
    const year = date.getFullYear()
    const month = date.getMonth()
    const firstDay = new Date(year, month, 1)
    const lastDay = new Date(year, month + 1, 0)
    const daysInMonth = lastDay.getDate()
    const startingDayOfWeek = firstDay.getDay()

    const days = []
    for (let i = 0; i < startingDayOfWeek; i++) days.push(null)
    for (let i = 1; i <= daysInMonth; i++) days.push(new Date(year, month, i))
    return days
  }

  const navigateMonth = (direction) => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + direction, 1))
  }

  const openYearPicker = () => {
    setShowYearPicker(true)
    setYearRangeStart(currentMonth.getFullYear() - 6)
  }

  const selectYear = (year) => {
    setCurrentMonth(new Date(year, currentMonth.getMonth(), 1))
    setShowYearPicker(false)
  }

  const years = Array.from({ length: 12 }, (_, i) => yearRangeStart + i)
  const days = getDaysInMonth(currentMonth)
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

  return (
    <div className="relative" ref={datePickerRef}>
      <div
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center justify-between w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-800 dark:text-white cursor-pointer hover:border-gray-400 dark:hover:border-gray-500 ${className}`}
      >
        <span className={selectedDate ? 'text-gray-900 dark:text-white' : 'text-gray-400'}>
          {selectedDate ? format(selectedDate, 'MMM dd, yyyy') : placeholder}
        </span>
        <Calendar className="w-5 h-5 text-gray-400" />
      </div>

      {isOpen && (
        <div className="absolute z-50 mt-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl p-4 w-80">
          {/* Month Navigation / Year Click */}
          <div className="flex items-center justify-between mb-4">
            <button
              type="button"
              onClick={() => navigateMonth(-1)}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <div className="flex items-center space-x-2">
              <h3 className="font-semibold text-gray-900 dark:text-white">{monthNames[currentMonth.getMonth()]}</h3>
              <button type="button" onClick={openYearPicker} className="text-sm text-gray-600 dark:text-gray-300 hover:underline">
                {currentMonth.getFullYear()}
              </button>
            </div>
            <button
              type="button"
              onClick={() => navigateMonth(1)}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>

          {showYearPicker ? (
            <div>
              <div className="flex items-center justify-between mb-3">
                <button type="button" onClick={() => setYearRangeStart(yearRangeStart - 12)} className="px-2 py-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700">Prev</button>
                <div className="text-sm text-gray-600 dark:text-gray-300">Select Year</div>
                <button type="button" onClick={() => setYearRangeStart(yearRangeStart + 12)} className="px-2 py-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700">Next</button>
              </div>
              <div className="grid grid-cols-4 gap-2">
                {years.map((y) => (
                  <button
                    key={y}
                    type="button"
                    onClick={() => selectYear(y)}
                    className={`py-2 text-sm rounded ${y === currentMonth.getFullYear() ? 'bg-primary-600 text-white' : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'}`}
                  >
                    {y}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <>
              {/* Day Names */}
              <div className="grid grid-cols-7 gap-1 mb-2">
                {dayNames.map((day) => (
                  <div key={day} className="text-center text-xs font-semibold text-gray-500 dark:text-gray-400 py-2">
                    {day}
                  </div>
                ))}
              </div>

              {/* Calendar Grid */}
              <div className="grid grid-cols-7 gap-1">
                {days.map((day, index) => {
                  if (!day) return <div key={index} className="py-2" />

                  const isSelected = selectedDate && format(day, 'yyyy-MM-dd') === format(selectedDate, 'yyyy-MM-dd')
                  const isToday = format(day, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd')
                  const isDisabled = (minDate && day < new Date(minDate)) || (maxDate && day > new Date(maxDate))

                  return (
                    <button
                      key={index}
                      type="button"
                      onClick={() => !isDisabled && handleDateSelect(day)}
                      disabled={isDisabled}
                      className={`py-2 text-sm rounded-md transition-colors ${
                        isSelected
                          ? 'bg-primary-600 text-white font-semibold'
                          : isToday
                          ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 font-semibold'
                          : isDisabled
                          ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                      }`}
                    >
                      {day.getDate()}
                    </button>
                  )
                })}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  )
}

export default DatePicker

